import {
  AdditionalInfoSection,
  Box,
  Button,
  DevelopInfoSection,
  DropdownOptions,
  FeaturesAndAmenitiesList,
  Icon,
  InventorySectionProvider,
  MapSection,
  ModalProvider,
  NameSection,
  NotFoundPage,
  PaginationContext,
  PaginationProvider,
  PropertyHistoryModal,
  PropertyPreviewNavigationTabs,
  SortProvider,
  Spinner,
  TooltipPopup,
} from "dash-ui";
import { formatByMask, PROPERTY_PHONE_NUMBER_MASK } from "dash-common";
import {
  GetPropertyByIdQuery,
  PropertyTransactionTypesEnum,
  useGetAmenitiesQuery,
  useGetPropertyByIdQuery,
  useGetPropertyChangelogsQuery,
} from "gql/generated.graphql";
import { useContext, useMemo, useRef, useState, ReactNode } from "react";
import { useTheme } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import SlideShowSection from "pages/properties/PropertyPreviewPage/components/SlideShowSection/SlideShowSection";
import { GeneralInfoSection } from "v2/components/property-general-info-section";
import InventoryTableSection from "pages/properties/PropertyPreviewPage/components/InventoryTableSection/InventoryTableSection";
import MarketingFilesSection from "pages/properties/PropertyPreviewPage/components/MarketingFilesSection/MarketingFilesSection";
import Header from "flow-components/Header/Header";
import moment from "moment";
import { useTranslation } from "react-i18next";

const ContactLink = ({
  email,
  children,
}: {
  email?: string;
  children: ReactNode;
}) => {
  return email ? <a href={`mailto: ${email}`}>{children}</a> : <>{children}</>;
};

export function PropertyPublicPreviewPage() {
  const theme = useTheme();
  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);

  // HTML Elements Reference
  const generalInfoRef = useRef(null);
  const additionalInfoRef = useRef(null);
  const marketingFilesInfoRef = useRef(null);

  // Routes States
  const params = useParams();
  const propertyId = params.id || "";

  const { getRefetchProps, state, setTotal, setPerPage } =
    useContext(PaginationContext);
  const pagination = getRefetchProps(state);

  // Navigation
  const navigate = useNavigate();

  const { t } = useTranslation(["pages", "common"]);

  // Queries and Mutations
  const getPropertyQuery = useGetPropertyByIdQuery({
    variables: { propertyId },
  });
  const getAmenitiesQuery = useGetAmenitiesQuery({
    variables: { filter: { propertyId } },
  });

  // Derived States
  const property = getPropertyQuery.data
    ?.getPropertyById as GetPropertyByIdQuery["getPropertyById"];
  const amenities = getAmenitiesQuery.data?.getAmenities.collection;

  const getChangelogsQuery = useGetPropertyChangelogsQuery({
    variables: {
      filter: { propertyId: property?._id },
      pagination,
      sort: [{ fieldName: "createdAt", sortOrder: -1 }],
    },
    fetchPolicy: "network-only",
    skip: !property?.hasChangelog || !isOpenHistoryModal,
  });

  const fromNow = useMemo(() => {
    const now = moment();
    const date = property?.lastUpdatedAtByChangelog;
    const diff = now.diff(date, "days");
    if (diff < 1) return "updated today";
    const string = `updated ${diff} days ago`;
    return string;
  }, [property]);

  const { history, totalCount } = useMemo(
    () => ({
      history: getChangelogsQuery.data?.getPropertyChangelogs?.collection || [],
      totalCount:
        getChangelogsQuery.data?.getPropertyChangelogs?.totalCount || 0,
    }),
    [getChangelogsQuery.data]
  );

  const isRent = useMemo(
    () => property?.transactionType === PropertyTransactionTypesEnum.ForRent,
    [property]
  );

  // Render HTML Loading State
  if (getPropertyQuery.loading) {
    return (
      <div>
        <Spinner fullContent />
      </div>
    );
  }

  // Render HTML Error State
  if (!getPropertyQuery.loading && (getPropertyQuery.error || !property)) {
    return (
      <NotFoundPage
        onRedirect={() =>
          navigate("/properties", { relative: "route", replace: true })
        }
      />
    );
  }

  // Render HTML Component
  return (
    <>
      <PaginationProvider perPage={5}>
        <ModalProvider>
          <>
            <DropdownOptions />
            <TooltipPopup />
            <PropertyHistoryModal
              isOpen={isOpenHistoryModal}
              onClose={() => setIsOpenHistoryModal(false)}
              loading={getChangelogsQuery.loading}
              data={history}
            />
            <>
              <div className="flex flex-col bg-slate-50">
                <Header withProfileDropdown={false} />
                <div className="w-full flex flex-col max-w-screen-lg mx-auto px-4 py-16 gap-8 text-slate-700">
                  <SlideShowSection property={property} />
                  <NameSection property={property} />
                  <div className="flex gap-4 w-full max-w-screen-lg">
                    <div className="flex flex-col gap-8 w-full md:max-w-[calc(100%-272px)]">
                      <PropertyPreviewNavigationTabs
                        top={48}
                        navigationElementsRefs={[
                          generalInfoRef,
                          additionalInfoRef,
                          marketingFilesInfoRef,
                        ]}
                      />
                      <GeneralInfoSection
                        containerRef={generalInfoRef}
                        data={{
                          highlights: property?.projectDetails || "",
                          overview: property?.overview || "",
                        }}
                      />
                      {amenities && (
                        <FeaturesAndAmenitiesList
                          amenities={amenities}
                          loading={getAmenitiesQuery.loading}
                        />
                      )}
                      <div>
                        <PaginationProvider perPage={5}>
                          <SortProvider>
                            <InventorySectionProvider>
                              <InventoryTableSection
                                filter={{ property }}
                                allowDownloads={false}
                              />
                            </InventorySectionProvider>
                          </SortProvider>
                        </PaginationProvider>
                      </div>
                      <AdditionalInfoSection
                        containerRef={additionalInfoRef}
                        property={property}
                      />
                      <MapSection property={property} />
                      <DevelopInfoSection property={property} />
                      <div className="w-full">
                        <MarketingFilesSection
                          property={property}
                          containerRef={marketingFilesInfoRef}
                          allowDownloads={false}
                        />
                      </div>
                    </div>
                    <div className="hidden md:flex flex-col gap-4 w-64 shrink-0">
                      <div className="bg-white shadow rounded-lg p-4 flex flex-col gap-4">
                        <button
                          className="flex items-center bg-sky-600 text-white rounded-lg w-full justify-center py-2 px-4 gap-4 disabled:cursor-not-allowed disabled:filter-grayscale"
                          disabled
                        >
                          <Icon name="folder" />
                          <span>Add to Portfolio</span>
                        </button>
                        <button
                          className="flex items-center justify-center py-2 px-4 gap-4 rounded-lg border w-full border-sky-600 text-sky-600 disabled:cursor-not-allowed disabled:filter-grayscale"
                          disabled
                        >
                          <Icon name="bookmarkBorder" />
                          <span>Track Property</span>
                        </button>
                      </div>
                      <div className="bg-white shadow rounded-lg p-4 flex flex-col gap-3">
                        <p className="font-bold">
                          Contact {isRent ? "Leasing Office" : "Development"}
                        </p>
                        <div className="flex gap-2 text-left items-center mt-1">
                          <Icon
                            name="mobile"
                            size={22}
                            color={theme.colors.g20}
                          />
                          <span>
                            {property.contactPhone?.number ? (
                              <>
                                {property.contactPhone?.dialCode || ""}{" "}
                                {formatByMask(
                                  property.contactPhone?.number || "",
                                  PROPERTY_PHONE_NUMBER_MASK
                                )}
                              </>
                            ) : (
                              <>Insert Phone</>
                            )}
                          </span>
                        </div>
                        {property?.contactEmail && (
                          <ContactLink email={property?.contactEmail || ""}>
                            <Button
                              colorVariation="success"
                              w="100%"
                              icon={"send"}
                              variation={"outline"}
                            >
                              {t("common:labels.email")}
                            </Button>
                          </ContactLink>
                        )}
                        <Button
                          colorVariation="success"
                          w="100%"
                          disabled={!property.website}
                          icon={"world"}
                          onClick={() =>
                            property.website && window.open(property.website)
                          }
                        >
                          {t("common:labels.website")}
                        </Button>
                      </div>
                      <div className="bg-white shadow rounded-lg p-4 flex flex-col gap-4">
                        <p className="font-bold">Last Updated</p>
                        <p className="flex gap-2">
                          <Icon
                            name="autorenew"
                            color={theme.colors.g20}
                            size={22}
                          />
                          <span>
                            {t("pages:propertyPreviewPage.historyLabel", {
                              date: fromNow,
                            })}
                          </span>
                        </p>
                        <button
                          disabled={!property?.hasChangelog}
                          onClick={() => setIsOpenHistoryModal(true)}
                          className="flex items-center justify-center px-4 py-2 rounded-lg border border-emerald-600 text-emerald-600"
                        >
                          History
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          </>
        </ModalProvider>
      </PaginationProvider>
    </>
  );
}
